import { useEffect, useMemo, useState, type FC } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  ButtonGroup,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Checkbox,
  Box,
  Text,
  Tooltip,
  Icon,
  CloseButton,
} from '@chakra-ui/react';
import { mdiMagnify } from '@mdi/js';
import { useDrawerStateDispatch } from 'features/main/context';
import { useAddMemberToTeams } from '../hooks/useAddMemberToTeams';
import { useOrgGroups } from 'features/user-group/user-group-list/api/useOrgGroups';
import { useSearchOnList } from 'features/common/hooks/search/useSearchOnList';
import { IGroup } from 'features/members/models';

interface AddToTeamsDrawerProps {
  onSuccess?: (addedTeamIds: string[]) => void;
}

interface TeamOptionProps {
  team: IGroup;
  isSelected: boolean;
  isAlreadyMember: boolean;
  onToggle: (teamId: string) => void;
}

const ADD_TO_TEAMS_SEARCH_FIELDS: Array<'name'> = [
  'name',
];

const TeamOption = ({
  team,
  isSelected,
  isAlreadyMember,
  onToggle,
}: TeamOptionProps) => (
  <Box
    p='3'
    borderWidth='1px'
    borderRadius='md'
    bg={isSelected ? 'purple.50' : 'white'}
    data-testid={`team-option-${team.id}`}
  >
    <Checkbox
      isChecked={isSelected}
      isDisabled={isAlreadyMember}
      onChange={() => onToggle(team.id)}
      data-testid={`team-checkbox-${team.id}`}
    >
      <Box ml='2' flex='1'>
        <Text fontWeight='medium'>{team.name}</Text>
        {team.description && (
          <Text fontSize='sm' color='gray.600' noOfLines={2}>
            {team.description}
          </Text>
        )}
        {isAlreadyMember && (
          <Text fontSize='xs' color='gray.500' mt='1'>
            Already a part of this group
          </Text>
        )}
      </Box>
    </Checkbox>
  </Box>
);

export const AddToTeamsDrawer: FC<AddToTeamsDrawerProps> = ({
  onSuccess,
}) => {
  const [{ drawerVisible, drawerId, data }] = useDrawerStateDispatch();
  const resolvedOnSuccess: ((addedTeamIds: string[]) => void) | undefined =
    onSuccess ??
    (typeof data?.onSuccess === 'function' ? data.onSuccess : undefined);
  const { data: groupsData, isLoading: groupsLoading } = useOrgGroups();
  const [searchValue, setSearchValue] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const {
    handleSubmit: originalHandleSubmit,
    isSubmitting,
    handleCloseDrawer,
  } = useAddMemberToTeams(
    selectedTeams,
    () => setSelectedTeams([]),
    resolvedOnSuccess,
  );

  const isOpen = drawerVisible && drawerId === 'add-member-to-teams-drawer';

  const handleSubmit = async () => {
    const success = await originalHandleSubmit();
    if (success) {
      // Close drawer immediately - refetch will happen in background
      handleCloseDrawer();
    }
  };

  // Get existingTeamIds from drawer data, defaulting to empty array
  // This will be updated when the drawer is opened with fresh data
  const existingTeamIds = data?.existingTeamIds || [];

  // Reset selected teams and search when drawer opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTeams([]);
      setSearchValue('');
    }
  }, [isOpen]);

  const allTeams: IGroup[] = useMemo(
    () =>
      (groupsData?.data || []).map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description,
      })),
    [groupsData?.data],
  );

  const filteredTeams = useSearchOnList(
    allTeams, 
    ADD_TO_TEAMS_SEARCH_FIELDS,
    searchValue,
  );

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId],
    );
  };

  const handleClearSearch = () => {
    setSearchValue('');
  };

  // Sort teams: selected ones first
  const sortedTeams = useMemo(() => {
    const selected = filteredTeams.filter((team) =>
      selectedTeams.includes(team.id),
    );
    const unselected = filteredTeams.filter(
      (team) => !selectedTeams.includes(team.id),
    );
    return [...selected, ...unselected];
  }, [filteredTeams, selectedTeams]);

  return (
    <Drawer isOpen={isOpen} onClose={handleCloseDrawer} size='lg'>
      <DrawerOverlay />
      <DrawerContent
        data-testid='add-member-to-teams-drawer'
        h='100vh'
        maxH='100vh'
        display='flex'
        flexDirection='column'
        overflow='hidden'
      >
        <DrawerHeader>Add to groups</DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody
          display='flex'
          flexDirection='column'
          gap={6}
          overflowY='auto'
          flex='1'
          minH='0'
          py={0}
          px={6}
          style={{ overscrollBehaviorY: 'contain' }}
          onWheelCapture={(e) => {
            // Prevent parent page from hijacking wheel when the drawer can scroll
            if (e.currentTarget.scrollHeight > e.currentTarget.clientHeight) {
              e.stopPropagation();
            }
          }}
        >
          <InputGroup>
            <InputLeftElement pointerEvents='none'>
              <Icon>
                <path d={mdiMagnify} />
              </Icon>
            </InputLeftElement>
            <Input
              placeholder='Search'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              data-testid='add-teams-search-input'
            />
            {searchValue.length > 0 && (
              <InputRightElement>
                <Tooltip label='Clear search'>
                  <CloseButton
                    onClick={handleClearSearch}
                    size='sm'
                    data-testid='add-teams-search-clear-button'
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>

          <Box flex='1' minH='0' pb={4} px={2}>
            {groupsLoading ? (
              <Box>Loading groups...</Box>
            ) : sortedTeams.length === 0 ? (
              <Text variant='subtle'>No groups found</Text>
            ) : (
              <VStack spacing={3} align='stretch'>
                {sortedTeams.map((team) => (
                  <TeamOption
                    key={team.id}
                    team={team}
                    isSelected={selectedTeams.includes(team.id)}
                    isAlreadyMember={existingTeamIds.includes(team.id)}
                    onToggle={handleTeamToggle}
                  />
                ))}
              </VStack>
            )}
          </Box>
        </DrawerBody>

        <DrawerFooter justifyContent='flex-end' flexShrink={0}>
          <ButtonGroup>
            <Button
              variant='ghost'
              onClick={handleCloseDrawer}
              isDisabled={isSubmitting}
              data-testid='add-teams-cancel-button'
            >
              Cancel
            </Button>
            <Button
              variant='solid'
              colorScheme='primary'
              onClick={() => handleSubmit()}
              isLoading={isSubmitting}
              isDisabled={isSubmitting || selectedTeams.length === 0}
              data-testid='add-teams-submit-button'
            >
              Add to {selectedTeams.length}{' '}
              {selectedTeams.length === 1 ? 'group' : 'groups'}
            </Button>
          </ButtonGroup>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
